(function() {
  "use strict";

  const BERLIN_TIME_ZONE = "Europe/Berlin";
  const BERLIN_CUTOFF_HOUR = 12;
  const BASE_TRANSIT_DAYS = 2;
  const EUROPE_EXTRA_DAYS = 3;
  const EUROPEAN_COUNTRIES = new Set([
    "AD", "AL", "AT", "AX", "BA", "BE", "BG", "BY", "CH", "CY", "CZ", "DE", "DK", "EE",
    "ES", "FI", "FO", "FR", "GB", "GG", "GI", "GR", "HR", "HU", "IE", "IM", "IS", "IT",
    "JE", "LI", "LT", "LU", "LV", "MC", "MD", "ME", "MK", "MT", "NL", "NO", "PL", "PT",
    "RO", "RS", "SE", "SI", "SJ", "SK", "SM", "UA", "VA"
  ]);
  const DELIVERY_WEEKDAYS = new Set([1, 2, 3, 4, 5, 6]);
  const DISPATCH_WEEKDAYS = new Set([1, 2, 3, 4, 5]);
  const INCOMING_EXTRA_DAYS = 7;

  function normalizeNumber(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function pad2(value) {
    return String(value).padStart(2, "0");
  }

  function getBerlinDateParts(input = new Date()) {
    const date = input instanceof Date ? input : new Date(input);
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: BERLIN_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      weekday: "short"
    });

    const parts = formatter.formatToParts(date);
    const lookup = Object.create(null);
    parts.forEach((part) => {
      lookup[part.type] = part.value;
    });

    const weekdayMap = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 };

    return {
      year: normalizeNumber(lookup.year),
      month: normalizeNumber(lookup.month),
      day: normalizeNumber(lookup.day),
      hour: normalizeNumber(lookup.hour),
      minute: normalizeNumber(lookup.minute),
      second: normalizeNumber(lookup.second),
      weekday: weekdayMap[lookup.weekday] ?? 0
    };
  }

  function toIsoDate(parts) {
    return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
  }

  function partsToDate(parts) {
    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0));
  }

  function addDays(parts, days) {
    const date = partsToDate(parts);
    date.setUTCDate(date.getUTCDate() + days);
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      weekday: date.getUTCDay()
    };
  }

  function getEasterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return { year, month, day };
  }

  function getBerlinHolidaySet(year) {
    const easterSunday = getEasterSunday(year);
    const goodFriday = addDays(easterSunday, -2);
    const easterMonday = addDays(easterSunday, 1);
    const ascensionDay = addDays(easterSunday, 39);
    const whitMonday = addDays(easterSunday, 50);

    return new Set([
      `${year}-01-01`,
      `${year}-03-08`,
      `${goodFriday.year}-${pad2(goodFriday.month)}-${pad2(goodFriday.day)}`,
      `${easterMonday.year}-${pad2(easterMonday.month)}-${pad2(easterMonday.day)}`,
      `${year}-05-01`,
      `${ascensionDay.year}-${pad2(ascensionDay.month)}-${pad2(ascensionDay.day)}`,
      `${whitMonday.year}-${pad2(whitMonday.month)}-${pad2(whitMonday.day)}`,
      `${year}-10-03`,
      `${year}-12-25`,
      `${year}-12-26`
    ]);
  }

  function isBerlinHoliday(parts) {
    return getBerlinHolidaySet(parts.year).has(toIsoDate(parts));
  }

  function isDispatchDay(parts) {
    return DISPATCH_WEEKDAYS.has(parts.weekday) && !isBerlinHoliday(parts);
  }

  function isDeliveryDay(parts) {
    return DELIVERY_WEEKDAYS.has(parts.weekday) && !isBerlinHoliday(parts);
  }

  function isDeliveryDayWithoutHoliday(parts) {
    return DELIVERY_WEEKDAYS.has(parts.weekday);
  }

  function getExtraTransitDays(countryCode) {
    const normalizedCode = String(countryCode || "").trim().toUpperCase();
    if (!normalizedCode || normalizedCode === "DE") return 0;
    return EUROPEAN_COUNTRIES.has(normalizedCode) ? EUROPE_EXTRA_DAYS : 0;
  }

  function normalizeVariantId(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const match = raw.match(/(\d+)$/);
    return match ? match[1] : raw;
  }

  function getNextDispatchDate(parts) {
    let cursor = { year: parts.year, month: parts.month, day: parts.day, weekday: parts.weekday };
    while (!isDispatchDay(cursor)) {
      cursor = addDays(cursor, 1);
    }
    return cursor;
  }

  function calculateBaselineDeliveryDate(dispatchDate, totalTransitDays) {
    let deliveryDate = dispatchDate;
    let daysRemaining = totalTransitDays;

    while (daysRemaining > 0) {
      deliveryDate = addDays(deliveryDate, 1);
      if (!isDeliveryDayWithoutHoliday(deliveryDate)) continue;
      daysRemaining -= 1;
    }

    return deliveryDate;
  }

  function calculateEstimate({ now = new Date(), countryCode = "DE", purchasable = true, isInStock = true } = {}) {
    if (!purchasable) return { status: "unavailable" };

    const berlinNow = getBerlinDateParts(now);
    let dispatchDate = {
      year: berlinNow.year,
      month: berlinNow.month,
      day: berlinNow.day,
      weekday: berlinNow.weekday
    };

    if (!isDispatchDay(dispatchDate) || berlinNow.hour >= BERLIN_CUTOFF_HOUR) {
      dispatchDate = getNextDispatchDate(addDays(dispatchDate, 1));
    }

    let deliveryDate = dispatchDate;
    const totalTransitDays = BASE_TRANSIT_DAYS + getExtraTransitDays(countryCode);
    let daysRemaining = totalTransitDays;

    while (daysRemaining > 0) {
      deliveryDate = addDays(deliveryDate, 1);
      if (isBerlinHoliday(deliveryDate)) continue;
      if (!isDeliveryDay(deliveryDate)) continue;
      daysRemaining -= 1;
    }

    const isIncoming = !isInStock;
    if (isIncoming) {
      let incomingDaysRemaining = INCOMING_EXTRA_DAYS;
      while (incomingDaysRemaining > 0) {
        deliveryDate = addDays(deliveryDate, 1);
        if (isBerlinHoliday(deliveryDate)) continue;
        if (!isDeliveryDay(deliveryDate)) continue;
        incomingDaysRemaining -= 1;
      }
    }

    const baselineDeliveryDate = calculateBaselineDeliveryDate(dispatchDate, totalTransitDays);

    return {
      status: "ok",
      deliveryDate,
      holidayDelay: toIsoDate(deliveryDate) !== toIsoDate(baselineDeliveryDate),
      extraTransitDays: getExtraTransitDays(countryCode),
      isInStock: Boolean(isInStock),
      isIncoming
    };
  }

  function formatEstimateDate(dateParts, locale) {
    const normalizedLocale = String(locale || "de-DE").trim().replace(/_/g, "-");
    let formatterLocale = normalizedLocale || "de-DE";

    if (formatterLocale.toLowerCase().startsWith("de")) {
      formatterLocale = "de-DE";
    } else if (!formatterLocale.toLowerCase().startsWith("en")) {
      formatterLocale = "en-US";
    }

    return new Intl.DateTimeFormat(formatterLocale, {
      timeZone: BERLIN_TIME_ZONE,
      weekday: "long",
      day: "numeric",
      month: "long"
    }).format(partsToDate(dateParts));
  }

  function initParahealthDelivery() {
    document.querySelectorAll("[data-ph-delivery]").forEach((container) => {
      if (container.dataset.initialized) return;
      container.dataset.initialized = "true";

      const mainText = container.querySelector("[data-ph-delivery-main]");
      const hintText = container.querySelector("[data-ph-delivery-hint]");
      const labelPrefix = container.dataset.labelPrefix || "";
      const variantsScriptId = container.dataset.variantsId;

      function loadVariantMap() {
        const script = document.getElementById(variantsScriptId);
        if (!script?.textContent?.trim()) return {};
        try {
          return JSON.parse(script.textContent);
        } catch (_error) {
          return {};
        }
      }

      function getCandidateVariantIds() {
        const selectors = document.querySelectorAll([
          '[name="id"]',
          '.product-form__variants select',
          '.variant-selectors select',
          '[data-variant-select]',
          '[data-variant-id][aria-selected="true"]',
          '[data-variant-id].selected',
          '[data-variant-id].is-selected'
        ].join(", "));
        const candidateIds = [];

        selectors.forEach((selector) => {
          if (selector.matches('[data-variant-id]')) {
            candidateIds.push(selector.getAttribute("data-variant-id"));
            return;
          }
          if ((selector.type === "radio" || selector.type === "checkbox") && !selector.checked) return;
          candidateIds.push(selector.value);
        });

        return candidateIds;
      }

      function getCurrentFormCountryCode() {
        const field = document.querySelector('input[name="country_code"], select[name="country_code"]');
        return String(field?.value || "").trim().toUpperCase();
      }

      function resolveVariantState() {
        const variantMap = loadVariantMap();
        let variantId = normalizeVariantId(container.dataset.variantId);

        getCandidateVariantIds().forEach((candidate) => {
          const normalized = normalizeVariantId(candidate);
          if (normalized && variantMap[normalized]) {
            variantId = normalized;
          }
        });

        container.dataset.variantId = variantId;

        const mapped = variantMap[variantId] || {};
        return {
          purchasable: mapped.purchasable !== false,
          isInStock: mapped.isInStock !== false
        };
      }

      function render() {
        if (!mainText) return;

        const variantState = resolveVariantState();
        const countryCode = String(
          getCurrentFormCountryCode() || window.Shopify?.country || container.dataset.countryCode || "DE"
        ).trim().toUpperCase();

        const estimate = calculateEstimate({
          now: new Date(),
          countryCode,
          purchasable: variantState.purchasable,
          isInStock: variantState.isInStock
        });

        if (estimate.status !== "ok") {
          container.hidden = true;
          container.classList.remove("is-fast", "is-incoming");
          return;
        }

        container.hidden = false;

        if (estimate.isIncoming) {
          const formattedDate = formatEstimateDate(estimate.deliveryDate, container.dataset.locale);
          mainText.textContent = container.dataset.labelIncoming || "Im Zulauf";
          if (hintText) {
            hintText.hidden = false;
            hintText.textContent = labelPrefix ? `${labelPrefix} ${formattedDate}` : formattedDate;
          }
          container.classList.remove("is-fast");
          container.classList.add("is-incoming");
        } else {
          const formattedDate = formatEstimateDate(estimate.deliveryDate, container.dataset.locale);
          mainText.textContent = labelPrefix ? `${labelPrefix} ${formattedDate}` : formattedDate;

          if (hintText) {
            if (estimate.holidayDelay && container.dataset.labelHolidayHint) {
              hintText.hidden = false;
              hintText.textContent = container.dataset.labelHolidayHint;
            } else {
              hintText.hidden = true;
              hintText.textContent = "";
            }
          }
          container.classList.remove("is-incoming");
          const isFast = estimate.isInStock && estimate.extraTransitDays === 0 && !estimate.holidayDelay;
          container.classList.toggle("is-fast", isFast);
        }
      }

      let renderTimer = 0;
      function scheduleRender() {
        clearTimeout(renderTimer);
        renderTimer = window.setTimeout(render, 80);
      }

      render();
      document.addEventListener("variant:changed", render);
      document.addEventListener("change", scheduleRender);
      document.addEventListener("input", scheduleRender);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initParahealthDelivery);
  } else {
    initParahealthDelivery();
  }
})();